import { iconMap } from "./mapIconsUtils";

export const generateFogWave = (
  startX,
  totalWidth,
  height = 200,
  waveHeight = 10,
  waveCount = 10
) => {
  const waveWidth = height / waveCount; // vertical wave
  let d = `M ${startX} 0 `;

  // Wavy left edge
  for (let i = 0; i <= waveCount; i++) {
    const y = i * waveWidth;
    const cpY = y - waveWidth / 2;
    const cpX = startX + (i % 2 === 0 ? -waveHeight : waveHeight);
    d += `Q ${cpX} ${cpY}, ${startX} ${y} `;
  }

  // Complete the rectangle to the right
  d += `L ${totalWidth} ${height} L ${totalWidth} 0 Z`;

  return d;
};

export const getBiome = (noise, x, y) => {
  return getBiomeFromNoise(noise, x, y);
};

export const getSmoothPath = (points, tension = 1) => {
  if (points.length < 2) return "";

  const d = [];

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1] || points[i];
    const p3 = points[i + 2] || p2;

    const cp1x = p1.x + ((p2.x - p0.x) / 6) * tension;
    const cp1y = p1.y + ((p2.y - p0.y) / 6) * tension;

    const cp2x = p2.x - ((p3.x - p1.x) / 6) * tension;
    const cp2y = p2.y - ((p3.y - p1.y) / 6) * tension;

    if (i === 0) {
      d.push(`M ${p1.x} ${p1.y}`);
    }

    d.push(`C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`);
  }

  return d.join(" ");
};

export const getBiomeFromNoise = (noise, x, y) => {
  const n = noise(x / 200, y / 200); // Noise from -1 to 1
  if (n > 0.5) return "mountain";
  if (n > 0.1) return "forest";
  if (n > -0.6) return "land";
  if (n > -0.7) return "seaside";
  return "water";
};

export const generateBiomeCanvasDataUrl = (width, height, noise, scale = 4) => {
  if (!width || !height || isNaN(width) || isNaN(height)) {
    console.warn(
      "Invalid width or height in generateBiomeCanvasDataUrl:",
      width,
      height
    );
    return null;
  }
  const canvas = document.createElement("canvas");
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext("2d");
  const imageData = ctx.createImageData(canvas.width, canvas.height);

  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const biome = getBiomeFromNoise(noise, x / scale, y / scale);
      const color = {
        mountain: [152, 194, 158], // Soft sage green
        forest: [172, 212, 174], // Light leafy green
        land: [196, 234, 194], // Pale minty green
        seaside: [255, 250, 200], // Warm soft yellow
        water: [173, 216, 230], // Pastel blue
      }[biome];

      const index = (y * canvas.width + x) * 4;
      imageData.data[index + 0] = color[0];
      imageData.data[index + 1] = color[1];
      imageData.data[index + 2] = color[2];
      imageData.data[index + 3] = 255;
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL();
};

export const generateMountainOverlayDataUrl = (
  width,
  height,
  noise,
  scale = 4
) => {
  if (!width || !height || isNaN(width) || isNaN(height)) {
    console.warn(
      "Invalid width or height in generateBiomeCanvasDataUrl:",
      width,
      height
    );
    return null;
  }
  const canvas = document.createElement("canvas");
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext("2d");

  // Fill base transparent
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const imageData = ctx.createImageData(canvas.width, canvas.height);

  const mountainColor = [152, 194, 158]; // Soft sage green
  const shadowColor = [0, 0, 0]; // base RGB for shadow

  const SHADOW_DEPTH = 6 * scale; // how far the shadow goes down
  const shadowAlphas = Array(SHADOW_DEPTH)
    .fill(0)
    .map((_, i) => Math.round(60 * (1 - i / SHADOW_DEPTH))); // fade out

  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const biome = getBiomeFromNoise(noise, x / scale, y / scale);
      const index = (y * canvas.width + x) * 4;

      if (biome === "mountain") {
        // Draw mountain base pixel
        imageData.data[index + 0] = mountainColor[0];
        imageData.data[index + 1] = mountainColor[1];
        imageData.data[index + 2] = mountainColor[2];
        imageData.data[index + 3] = mountainColor[3];

        // Draw shadows below
        for (let i = 1; i <= SHADOW_DEPTH; i++) {
          const sy = y + i;
          if (sy >= canvas.height) break;
          const shadowIdx = (sy * canvas.width + x) * 4;
          const alpha = shadowAlphas[i - 1];

          // Blend only if not already opaque
          if (imageData.data[shadowIdx + 3] === 0) {
            imageData.data[shadowIdx + 0] = shadowColor[0];
            imageData.data[shadowIdx + 1] = shadowColor[1];
            imageData.data[shadowIdx + 2] = shadowColor[2];
            imageData.data[shadowIdx + 3] = alpha;
          }
        }
      }
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL();
};

export const renderDecorShape = (d) => {
  switch (d.type) {
    case "tree":
      return (
        <g>
          <rect x={d.x - 2} y={d.y} width={4} height={10} fill="#8B5A2B" />
          <circle cx={d.x} cy={d.y - 4} r={6} fill="#2E8B57" />
          <circle cx={d.x - 4} cy={d.y - 2} r={4} fill="#2E8B57" />
          <circle cx={d.x + 4} cy={d.y - 2} r={4} fill="#2E8B57" />
        </g>
      );

    case "moutain":
    case "mountain":
    case "tree_1":
      return (
        <image
          href={iconMap["arbre_1"]}
          x={d.x - 15}
          y={d.y - 15}
          width={35}
          height={35}
        />
      );
    case "tree_2":
      return (
        <image
          href={iconMap["arbre_2"]}
          x={d.x - 15}
          y={d.y - 15}
          width={30}
          height={30}
        />
      );
    case "bush_1":
      return (
        <image
          href={iconMap["buisson"]}
          x={d.x - 10}
          y={d.y - 10}
          width={20}
          height={20}
        />
      );
    case "batiment_1":
    case "batiment_2":
    case "batiment_3":
    case "batiment_4":
    case "batiment_5":
      return (
        <image
          href={iconMap[d.type]}
          x={d.x - 25}
          y={d.y - 25}
          width={50}
          height={50}
        />
      );

    case "pebbles":
      return (
        <g fill="#777">
          <path
            d={`M ${d.x - 4},${d.y + 2}
               Q ${d.x - 6},${d.y - 2} ${d.x - 2},${d.y - 3}
               Q ${d.x + 0},${d.y - 1} ${d.x - 1},${d.y + 2}
               Z`}
          />
          <path
            d={`M ${d.x},${d.y}
               Q ${d.x - 1},${d.y - 2} ${d.x + 2},${d.y - 2}
               Q ${d.x + 4},${d.y} ${d.x + 2},${d.y + 2}
               Q ${d.x},${d.y + 1} ${d.x},${d.y}
               Z`}
          />
          <path
            d={`M ${d.x + 5},${d.y + 1}
               Q ${d.x + 6},${d.y - 2} ${d.x + 9},${d.y - 1}
               Q ${d.x + 10},${d.y + 1} ${d.x + 7},${d.y + 3}
               Q ${d.x + 5},${d.y + 3} ${d.x + 5},${d.y + 1}
               Z`}
          />
        </g>
      );

    case "small-rock":
      return (
        <path
          d={`M ${d.x - 4},${d.y}
           Q ${d.x - 2},${d.y - 6} ${d.x + 1},${d.y - 5}
           Q ${d.x + 4},${d.y - 3} ${d.x + 3},${d.y + 2}
           Q ${d.x + 0},${d.y + 4} ${d.x - 3},${d.y + 2}
           Z`}
          fill="#888"
        />
      );

    case "big-rock":
      return (
        <path
          d={`M ${d.x - 6},${d.y}
           Q ${d.x - 3},${d.y - 10} ${d.x + 2},${d.y - 8}
           Q ${d.x + 8},${d.y - 5} ${d.x + 5},${d.y + 4}
           Q ${d.x + 1},${d.y + 8} ${d.x - 5},${d.y + 5}
           Z`}
          fill="#999"
        />
      );

    case "confogaz":
    case "elonova":
    case "eopur":
    case "filacom":
    case "info_tec_services":
    case "iserba":
    case "reso_confort":
    case "sanilogis":
    case "sevanova":
    case "smecso":
    case "tekinova":
    case "unica":
    case "vizavy":
      return (
        <image
          href={iconMap[d.type]}
          x={d.x - 10}
          y={d.y - 20}
          width={20}
          height={25}
        />
      );

    default:
      return null;
  }
};
